import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import CardHeader from "../components/CardHeader";
import PageHeader from "../../../../core/common/crmComponents/PageHeader";
import PaginationControls from "../../../../core/common/pagination";
import AddLead from "../actions/AddLead";
import EditLead from "../actions/EditLead";
import DeleteLead from "../actions/DeleteLead";
import LeadList from "../components/LeadList";
import LeadGridLayout from "../components/LeadGridLayout";
import { useLeadsData } from "../hooks/useLeadsData";
import LoaderComponent from "../../../../core/common/crmComponents/LoaderComponent";
import { useSearchParams } from "react-router-dom";
import {
  MODULES,
  MODULES_ACTIONS,
} from "../../../../core/data/constants/moduleConstants";
import HasPermission from "../../../../core/common/wrapper/HasPermission";
import {
  getUrlParams,
  setUrlParams,
} from "../../../../utils/helpers/setAndGetUrlParams";
import { generateQueryParams } from "../../../../utils/helpers/generateQueryParams";
import ZoomStatusLegend from "../../../../core/common/crmComponents/zoomMeeting/ZoomStatusLegend";
import { useFunctionalityAccess } from "../../../../core/common/wrapper/HasFunctionalityAccess";
import queryClient from "../../../../config/queryClient";
import { setReloading } from "../../../../core/data/redux/moduleDataSlice";

const Leads = () => {
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const user = JSON.parse(localStorage.getItem("user") || []);
  const [layout, setLayout] = useState("list");
  const { companyNameArr } = useSelector((state) => state.auth);
  const hasZoomAccess = useFunctionalityAccess("zoomSetting");
  const reloading = useSelector((state) => state.moduleData.reloading);

  const initialLimit = getUrlParams("limit")
    ? Math.ceil(getUrlParams("limit"))
    : 50;
  const initialPage = getUrlParams("page") ? Number(getUrlParams("page")) : 1;
  const initialCompany = getUrlParams("companyid")
    ? getUrlParams("companyid")
    : user?.ofCompany?._id ||
      (companyNameArr.length > 0 ? companyNameArr[0].value : null);

  const [limit, setLimit] = useState(initialLimit);
  const [page, setPage] = useState(initialPage);
  const [selectedCompany, setSelectedCompany] = useState(initialCompany);

  const toggleLayout = () => {
    setLayout((prevLayout) => (prevLayout === "list" ? "grid" : "list"));
  };

  const { filters, hasNewFilters } = useSelector((state) => state.filter);
  const leadsFilters = filters.leads || [];

  const { queryParams, params } = generateQueryParams(leadsFilters);

  const { data, isLoading, isError, error, refetch, isFetching } = useLeadsData(
    {
      page: page ? page : 1,
      limit: limit ? limit : 50,
      ofCompany: selectedCompany ? selectedCompany : null,
      queryString: params.toString(),
      queryParams: queryParams.toString(),
    }
  );

  const leadsData = data?.data?.data;
  const [filteredData, setFilteredData] = useState(leadsData);

  const totalCount = data?.data?.totalResult;
  const totalPages = Math.ceil(totalCount / limit);

  useEffect(() => {
    if (!searchParams.get("companyid")) {
      setUrlParams(setSearchParams, "companyid", initialCompany);
    }
    setSelectedCompany(initialCompany);
  }, [initialCompany]);

  useEffect(() => {
    if (totalPages > 0 && page > totalPages) {
      setPage(totalPages); // Reset to the last available page
      setUrlParams(setSearchParams, "page", totalPages);
    }
  }, [totalPages, page, setSearchParams]);

  useEffect(() => {
    if (searchParams.get("page") !== String(page)) {
      setUrlParams(setSearchParams, "page", page);
    }
  }, [page]);

  useEffect(() => {
    if (selectedCompany) {
      setUrlParams(setSearchParams, "companyid", selectedCompany);
    }
  }, [selectedCompany]);

  useEffect(() => {
    if (
      searchParams.get("page") ||
      searchParams.get("limit") !== String(limit)
    ) {
      setUrlParams(setSearchParams, "limit", limit);
    }
  }, [limit]);

  const handlePageChange = (newPage) => {
    setPage(newPage); // Update page state to trigger refetch
  };

  const handleLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1); // Reset page to 1 whenever limit changes
  };

  useEffect(() => {
    if (leadsFilters?.length > 0 && hasNewFilters) {
      setPage(1);
    } else {
      setPage(page);
    }
    setLimit(limit);
  }, [leadsFilters]);

  useEffect(() => {
    if (reloading) {
      queryClient.invalidateQueries(["leads"]);
      dispatch(setReloading(false));
    }
  }, [reloading, dispatch]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content custom-content-padding">
          <div className="row">
            <div className="col-md-12">
              <PageHeader
                pageName="Leads"
                totalCount={totalCount || 0}
                {...(hasZoomAccess && {
                  children2: <ZoomStatusLegend />,
                })}
              />

              <div className="card">
                <CardHeader
                  layout={layout}
                  toggleLayout={toggleLayout}
                  selectedCompany={selectedCompany}
                  setSelectedCompany={setSelectedCompany}
                />
                <div className="card-body py-2">
                  <PaginationControls
                    totalPages={totalPages}
                    totalCount={totalCount}
                    currentPageDataCount={
                      filteredData?.length > 0
                        ? filteredData?.length
                        : Math.min(leadsData?.length, limit)
                    }
                    onPageChange={handlePageChange}
                    onLimitChange={handleLimitChange}
                    moduleName={MODULES.LEADS}
                  />

                  {isLoading ? (
                    <LoaderComponent />
                  ) : isError ? (
                    <div>Error: {error.message}</div>
                  ) : layout === "list" ? (
                    <LeadList
                      leadsData={leadsData}
                      selectedCompany={selectedCompany}
                      setFilteredData={setFilteredData}
                    />
                  ) : (
                    <LeadGridLayout leadsData={leadsData} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <HasPermission module={MODULES.LEADS} action={MODULES_ACTIONS.CREATE}>
        <AddLead />
      </HasPermission>

      <DeleteLead />
    </>
  );
};

export default Leads;
